import React, { useState, useEffect } from "react";
import payrollService from "../../services/payrollService";
import TaxSlabEditor from "../../components/Payroll/TaxSlabEditor";
import Notification from "../../components/UI/Notification";
import { Plus, Edit2, Trash2, Copy } from "lucide-react";
import ReactPaginate from "react-paginate";

const TaxConfigurationsNew = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    type: "INCOME_TAX",
    code: "",
    name: "",
    jurisdiction_country: "IN",
    jurisdiction_state: "",
    effective_from: "",
    effective_to: "",
    rate_type: "SLAB",
    rate_value: null,
    employee_share: null,
    employer_share: null,
    surcharge_percent: null,
    cess_percent: null,
    is_active: true,
    version: 1,
    slabs: [],
  });
  const [filters, setFilters] = useState({ type: "", state: "", active: "" });
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });

  useEffect(() => {
    fetchConfigs();
  }, [filters, pagination.current_page]);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await payrollService.getAllTaxConfigs({
        ...filters,
        page: pagination.current_page,
        per_page: 25,
      });
      
      console.log("Tax configs API response:", response);
      
      // Handle both direct data and paginated response structures
      let configsData = [];
      let paginationData = {};
      
      // Check if response has success property or if data exists directly
      if (response.success || response.data) {
        if (response.data && response.data.data) {
          // Paginated response structure (like your API response)
          configsData = response.data.data || [];
          paginationData = {
            current_page: response.data.current_page || 1,
            last_page: response.data.last_page || 1,
            total: response.data.total || 0,
          };
        } else if (Array.isArray(response.data)) {
          // Direct array response
          configsData = response.data;
          paginationData = {
            current_page: 1,
            last_page: 1,
            total: response.data.length,
          };
        }
        
        console.log("Processed configs data:", configsData);
        setConfigs(configsData);
        setPagination(paginationData);
      } else {
        console.log("No data found in response");
        setConfigs([]);
      }
    } catch (error) {
      console.error("Error fetching tax configurations:", error);
      setNotification({ type: "error", message: "Failed to fetch tax configurations" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await payrollService.updateTaxConfig(editingId, form);
        setNotification({ type: "success", message: "Tax configuration updated" });
      } else {
        await payrollService.createTaxConfig(form);
        setNotification({ type: "success", message: "Tax configuration created" });
      }
      setShowModal(false);
      resetForm();
      fetchConfigs();
    } catch (error) {
      setNotification({ type: "error", message: "Operation failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tax configuration?")) return;
    try {
      await payrollService.deleteTaxConfig(id);
      setNotification({ type: "success", message: "Tax configuration deleted" });
      fetchConfigs();
    } catch (error) {
      setNotification({ type: "error", message: "Failed to delete" });
    }
  };

  const handleDuplicate = (config) => {
    setForm({
      ...config,
      code: `${config.code}_COPY`,
      name: `${config.name} (Copy)`,
      version: config.version + 1,
      slabs: config.slabs || [],
    });
    setEditingId(null);
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      type: "income_tax",
      code: "",
      name: "",
      jurisdiction_country: "IN",
      jurisdiction_state: "",
      effective_from: "",
      effective_to: "",
      rate_type: "SLAB",
      rate_value: null,
      employee_share: null,
      employer_share: null,
      surcharge_percent: null,
      cess_percent: null,
      is_active: true,
      version: 1,
      slabs: [],
    });
    setEditingId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const getTypeLabel = (type) => {
    const labels = {
      income_tax: "Income Tax",
      pf: "Provident Fund (PF)",
      esi: "Employee State Insurance (ESI)",
      professional_tax: "Professional Tax (PT)",
    };
    return labels[type] || type;
  };

  const openEditModal = (config) => {
    setForm({
      type: config.type,
      code: config.code || "",
      name: config.name,
      jurisdiction_country: config.jurisdiction_country,
      jurisdiction_state: config.jurisdiction_state || "",
      effective_from: formatDate(config.effective_from),
      effective_to: formatDate(config.effective_to) || "",
      rate_type: config.rate_type,
      rate_value: config.rate_value || null,
      employee_share: config.employee_share || null,
      employer_share: config.employer_share || null,
      surcharge_percent: config.surcharge_percent || null,
      cess_percent: config.cess_percent || null,
      is_active: config.is_active,
      version: config.version,
      slabs: config.slabs || [],
    });
    setEditingId(config.id);
    setShowModal(true);
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Tax Configurations
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage tax rules and slabs
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
        >
          <Plus size={18} />
          Add Tax Configuration
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Types</option>
            <option value="income_tax">Income Tax</option>
            <option value="pf">Provident Fund (PF)</option>
            <option value="esi">Employee State Insurance (ESI)</option>
            <option value="professional_tax">Professional Tax (PT)</option>
          </select>
          <input
            type="text"
            placeholder="Filter by State (e.g., MH, KA, DL)"
            value={filters.state}
            onChange={(e) => setFilters({ ...filters, state: e.target.value })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
          <select
            value={filters.active}
            onChange={(e) => setFilters({ ...filters, active: e.target.value })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Jurisdiction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Effective From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : configs.length > 0 ? (
                configs.map((config) => (
                  <tr key={config.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {config.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {config.code ? `${config.code} ` : ""}(v{config.version})
                        {config.rate_type !== "SLAB" && config.slabs?.length === 0 && (
                          <span className="ml-2 text-blue-600">
                            {config.rate_type === "PERCENT" ? `${config.rate_value}%` : 
                             config.rate_type === "FIXED" ? `₹${parseFloat(config.rate_value || 0).toLocaleString("en-IN")}` : ""}
                          </span>
                        )}
                        {config.slabs?.length > 0 && (
                          <span className="ml-2 text-blue-600">
                            {config.slabs.length} slabs
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {getTypeLabel(config.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {config.jurisdiction_country}
                      {config.jurisdiction_state && ` - ${config.jurisdiction_state}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(config.effective_from)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          config.is_active
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {config.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(config)}
                          className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDuplicate(config)}
                          className="text-green-600 hover:text-green-700 dark:text-green-400"
                          title="Duplicate for new version"
                        >
                          <Copy size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(config.id)}
                          className="text-red-600 hover:text-red-700 dark:text-red-400"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No tax configurations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              pageCount={pagination.last_page}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={({ selected }) =>
                setPagination((prev) => ({ ...prev, current_page: selected + 1 }))
              }
              containerClassName="flex items-center justify-center gap-2"
              pageClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              pageLinkClassName="text-gray-700 dark:text-gray-300"
              previousClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              previousLinkClassName="text-gray-700 dark:text-gray-300"
              nextClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              nextLinkClassName="text-gray-700 dark:text-gray-300"
              activeClassName="bg-blue-600 text-white hover:bg-blue-700"
              disabledClassName="opacity-50 cursor-not-allowed"
            />
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full my-8">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingId ? "Edit" : "Add"} Tax Configuration
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type *
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="income_tax">Income Tax</option>
                    <option value="pf">Provident Fund (PF)</option>
                    <option value="esi">Employee State Insurance (ESI)</option>
                    <option value="professional_tax">Professional Tax (PT)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Code *
                  </label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    value={form.jurisdiction_country}
                    onChange={(e) => setForm({ ...form, jurisdiction_country: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={form.jurisdiction_state}
                    onChange={(e) => setForm({ ...form, jurisdiction_state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Effective From *
                  </label>
                  <input
                    type="date"
                    value={form.effective_from}
                    onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Effective To
                  </label>
                  <input
                    type="date"
                    value={form.effective_to}
                    onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rate Type *
                  </label>
                  <select
                    value={form.rate_type}
                    onChange={(e) => setForm({ ...form, rate_type: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="SLAB">Slab Based</option>
                    <option value="FIXED">Fixed Amount</option>
                    <option value="PERCENT">Percentage</option>
                  </select>
                </div>
                {form.rate_type !== "SLAB" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rate Value {form.rate_type === "PERCENT" ? "(%)" : form.rate_type === "FIXED" ? "(₹)" : ""}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.rate_value || ""}
                      onChange={(e) => setForm({ ...form, rate_value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder={form.rate_type === "PERCENT" ? "e.g., 12.00" : "e.g., 200.00"}
                    />
                  </div>
                )}
                
                {/* Employee & Employer Share (for PF, ESI, etc.) */}
                {(form.type === "pf" || form.type === "esi") && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Employee Share (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={form.employee_share || ""}
                        onChange={(e) => setForm({ ...form, employee_share: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., 12.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Employer Share (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={form.employer_share || ""}
                        onChange={(e) => setForm({ ...form, employer_share: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., 12.00"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cess Percent (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.cess_percent || ""}
                    onChange={(e) => setForm({ ...form, cess_percent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., 4.00"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Surcharge Percent (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.surcharge_percent || ""}
                    onChange={(e) => setForm({ ...form, surcharge_percent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., 10.00"
                  />
                </div>
              </div>

              {/* Tax Slabs */}
              {form.rate_type === "SLAB" && (
                <div className="mt-6">
                  <TaxSlabEditor
                    slabs={form.slabs}
                    onChange={(slabs) => setForm({ ...form, slabs })}
                  />
                </div>
              )}

              <div className="pt-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Active
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxConfigurationsNew;

